/**
 * Types for the Configuration -> Substitutes page and the substitute endpoint behind it.
 */

/** The periods the page can narrow the list down to. */
export const STATUS_FILTER = {
    ALL: "all",
    CURRENT: "current",
    UPCOMING: "upcoming"
} as const;

export type StatusFilterValue = typeof STATUS_FILTER[keyof typeof STATUS_FILTER];

/** What the endpoint returns for one substitution. */
export interface Substitute {
    userId: number;
    userName: string;
    substituteId: number;
    substituteName: string;
    /** no start date means the substitution is neither running nor planned */
    from: string | null;
    /** no end date means it runs indefinitely */
    to: string | null;
    version: number;
    /** a removed substitution is one saved back as inactive, which filters it out of the list */
    active?: boolean;
}

/** One page of the list, as the endpoint serves it. */
export interface SubstitutesPage {
    /** the page that was served, echoed back by the endpoint */
    page: number;
    /** number of matching rows, across every page */
    records: number;
    rows: Substitute[];
}

/** The sort the endpoint takes: one field, ascending when `sortOrder` is true. */
export interface SubstitutesSort {
    fieldName: string;
    sortOrder: boolean;
}

/** One substitution, with every cell already formatted the way the column shows it. */
export interface SubstituteGridRow {
    id: string;
    /** where the row sits in the loaded list, which is how the edit and delete actions address it */
    index: number;
    user: string;
    substitute: string;
    notBefore: string;
    notAfter: string;
    status: string;
}

/** The two columns that carry a person, which are rendered as a name over an email. */
export type PersonField = "user" | "substitute";

/** The redux slice behind both renderings of the page. */
export interface SubstitutesState {
    activeFilter: StatusFilterValue;
    /** one of LOADING_STATUS */
    loadingStatus: number;
    substitutes: SubstitutesPage;
    searchValue: string;
    sort: SubstitutesSort;
}

/** What the list selector hands a page: the rows loaded so far, and where the paging stands. */
export interface SubstitutesForRender {
    allSubstitutes: Substitute[];
    records: number;
    page: number;
}

/**
 * Anything the slice's reducer takes. The payload is read back per case, the way the other
 * reducers in the app do it, since a single action shape covers all of them.
 */
export interface SubstitutesAction {
    type: string;
    data?: SubstitutesPage | StatusFilterValue | SubstitutesSort | string | null;
}
